import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  resolveReentryPlanningTabFromHash,
  resolveReentryPlanningTabHash,
} from '../utils/reentry-planning-tab-hash.util';

/**
 * Keeps the Re-Entry Planning active tab and the URL hash in sync so `/reentry-plans#forms`
 * (Forms sub-page back navigation, refresh, direct links) opens the Forms tab.
 * @param {string} defaultTab - Tab label used when the hash does not name a tab.
 * @returns {{ activeTab: string, selectTab: (tabLabel: string) => void }}
 */
export const useReentryPlanningTabHash = (defaultTab) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(
    () => resolveReentryPlanningTabFromHash(location.hash) ?? defaultTab,
  );

  // Re-sync when the hash changes while mounted (e.g. the URL is edited in place).
  useEffect(() => {
    const hashTab = resolveReentryPlanningTabFromHash(location.hash);
    if (hashTab) {
      setActiveTab(hashTab);
    }
  }, [location.hash]);

  const selectTab = useCallback(
    (tabLabel) => {
      setActiveTab(tabLabel);
      const hash = resolveReentryPlanningTabHash(tabLabel);
      if (hash === location.hash) {
        return;
      }
      navigate({ pathname: location.pathname, search: location.search, hash }, { replace: true });
    },
    [navigate, location.pathname, location.search, location.hash],
  );

  return { activeTab, selectTab };
};
